import styles from "./PaymentPlans.module.scss";
import { Dispatch, FC, SetStateAction, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { backgroundColorState, darkerBackgroundColorState, greyLighterState, lighterBackgroundColorState, textColorState, themeState, userDataState } from "@/lib/store";
import { Plan } from "@/models/subscription";
import { plansFeatures } from "@/lib/vars";
import { Button, ButtonGroup, Card, CardContent, Divider, Skeleton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import VerifiedIcon from "@mui/icons-material/Verified";
import AddIcon from "@mui/icons-material/Add";
import { useUpdateEffect } from "react-use";
import { classNames } from "@/lib/utils";
import { startTour } from "@/components/tour/TourGuide";

interface PropsTypes {
    interval: "annually" | "monthly";
    plan: Plan;
    plans: Plan[];
    setInterval: Dispatch<SetStateAction<"annually" | "monthly">>;
    setPlan: Dispatch<SetStateAction<Plan>>;
    setStep: Dispatch<SetStateAction<number>>;
    onClose: (close: boolean) => void;
}

const PaymentPlans: FC<PropsTypes> = ({ interval, plan: selectedPlan, plans, setInterval, setPlan, setStep, onClose }) => {
    const lighterBackgroundColor = useRecoilValue(lighterBackgroundColorState);
    const darkerBackgroundColor = useRecoilValue(darkerBackgroundColorState);
    const backgroundColor = useRecoilValue(backgroundColorState);

    const userData = useRecoilValue(userDataState);
    const textColor = useRecoilValue(textColorState);
    const greyLighter = useRecoilValue(greyLighterState);
    const theme = useRecoilValue(themeState);
    const grey = theme === "dark" ? "#939598" : "#808080";

    useUpdateEffect(() => setPlan(plans ? plans.find((plan) => plan.product_name === selectedPlan?.product_name && plan.plan_interval === interval) : undefined), [interval]);

    return (
        <Box className={styles.PaymentPlansPage}>
            <style jsx>{`
                button {
                    color: ${textColor};
                }
            `}</style>
            <ButtonGroup>
                <Button fullWidth={true} onClick={plans ? () => setInterval("annually") : () => {}} variant={interval === "annually" ? "contained" : "outlined"} sx={{ textTransform: "initial", color: textColor, fontSize: "16px" }}>
                    Yearly
                </Button>
                <Button fullWidth={true} onClick={plans ? () => setInterval("monthly") : () => {}} variant={interval === "annually" ? "outlined" : "contained"} sx={{ textTransform: "initial", color: textColor, fontSize: "16px" }}>
                    Monthly
                </Button>
            </ButtonGroup>

            {/* Plan cards — the original two-card layout is the selector
                again. The previous experiment of using a comparison grid
                directly as the picker was reverted on user feedback;
                we kept FreeVsPaidComparison.tsx around in case it's
                useful elsewhere but it's no longer mounted here. New
                offering items (Market Intelligence, ORTEX AI assistant
                with "Limited" qualifier, Excel addin, API access "with
                API subscription") are now folded into plansFeatures in
                lib/vars.ts so they appear inside the cards rather than
                in a separate matrix. */}
            <Box className={styles["plan-selector"]}>
                {plans
                    ? plans
                          .filter((plan) => plan.plan_interval === interval)
                          .map((plan) => {
                              const disabled = userData?.subscription_plan === "ORTEX Basic" && plan.product_name === "ORTEX Basic";
                              return (
                                  <Card
                                      key={plan.plan_id}
                                      sx={{
                                          border: plan.plan_id === selectedPlan?.plan_id ? `2px solid #31aba6` : `2px solid ${theme === "light" ? lighterBackgroundColor : backgroundColor}`,
                                          "&:hover": { backgroundColor: darkerBackgroundColor, borderColor: plan.plan_id !== selectedPlan?.plan_id && darkerBackgroundColor, cursor: "pointer" },
                                          padding: 1,
                                      }}
                                      elevation={3}
                                      className={styles.card}
                                      onClick={() => setPlan(plan)}
                                  >
                                      <CardContent sx={{ height: "100%" }}>
                                          <Box>
                                              <Typography variant="body1" component="span" className={styles.name}>
                                                  <Typography variant="h1" component="span">
                                                      {/* Drop the "ORTEX " prefix on the
                                                          card title — the page header
                                                          already establishes the brand and
                                                          the prefix made every card title
                                                          start with the same word. The
                                                          underlying `plan.product_name`
                                                          still carries it (used as a key
                                                          into plansFeatures, sent back to
                                                          Stripe etc.); this is purely a
                                                          display strip. */}
                                                      {plan.product_name.replace(/^ORTEX\s+/i, "")}
                                                  </Typography>
                                                  {disabled && (
                                                      <Typography variant="body1" component="span" sx={{ color: grey }}>
                                                          Your current plan
                                                      </Typography>
                                                  )}
                                              </Typography>

                                              <Box className={styles.features}>
                                                  {plan.product_name.includes("Advanced") && (
                                                      <Typography variant="body1" component="span">
                                                          Everything in Basic
                                                          <Typography variant="body1" component="span" style={{ marginLeft: 5, display: "flex" }}>
                                                              <AddIcon color={"primary"} fontSize="medium" />
                                                          </Typography>
                                                      </Typography>
                                                  )}
                                                  {(plansFeatures[plan.product_name] || plansFeatures["ORTEX Basics"]).map((feature: { text: string; qualifier?: string }) => (
                                                      <Typography variant="body1" component="span" key={feature.text}>
                                                          <VerifiedIcon color={"primary"} fontSize="small" sx={{ marginRight: "5px" }} />
                                                          {feature.text}
                                                          {feature.qualifier && (
                                                              <Typography
                                                                  component="span"
                                                                  sx={{
                                                                      marginLeft: 0.75,
                                                                      opacity: 0.65,
                                                                      fontSize: "0.85em",
                                                                      fontStyle: "italic",
                                                                  }}
                                                              >
                                                                  ({feature.qualifier})
                                                              </Typography>
                                                          )}
                                                      </Typography>
                                                  ))}
                                              </Box>
                                              <Divider className={styles.line} sx={{ borderColor: greyLighter }} />

                                              <Typography variant="body1" component="span" sx={{ fontSize: "22px" }}>
                                                  {plan.currency_symbol} {interval === "annually" ? Math.round(plan.plan_price / 12) : plan.plan_price}
                                                  <Typography variant="body1" component="span" sx={{ color: grey, fontSize: "14px", alignSelf: "flex-end " }}>
                                                      /month
                                                  </Typography>
                                              </Typography>

                                              <Typography variant="body1" component="span" sx={{ color: grey, fontSize: "14px" }}>
                                                  {interval === "annually" ? `Paid anually at ${plan.currency_symbol} ${plan.plan_price}` : "\u00A0"}
                                              </Typography>
                                          </Box>
                                      </CardContent>
                                  </Card>
                              );
                          })
                    : [0, 1].map((index) => (
                          <Card
                              key={index}
                              sx={{
                                  border: `2px solid ${theme === "light" ? lighterBackgroundColor : backgroundColor}`,
                                  padding: 1,
                              }}
                              elevation={3}
                              className={classNames(styles.card, styles["skeleton-card"])}
                          >
                              <CardContent sx={{ height: "100%" }}>
                                  <Box>
                                      <Typography variant="body1" component="span" className={styles.name}>
                                          <Typography variant="h1" component="span">
                                              <Skeleton width={210} />
                                          </Typography>
                                      </Typography>

                                      <Box className={styles.features}>
                                          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((featureIndex) => (
                                              <Typography variant="body1" component="span" key={featureIndex}>
                                                  <VerifiedIcon color={"primary"} fontSize="small" sx={{ marginRight: "5px" }} />
                                                  <Skeleton width={150} height={24} sx={{ marginBottom: "4px" }} />
                                              </Typography>
                                          ))}
                                      </Box>
                                      <Divider className={styles.line} sx={{ borderColor: greyLighter }} />

                                      <Typography variant="body1" component="span" sx={{ fontSize: "22px" }}>
                                          <Skeleton width={60} height={32} />
                                      </Typography>

                                      <Typography variant="body1" component="span" sx={{ color: grey, fontSize: "14px" }}>
                                          <Skeleton width={130} height={20} />
                                      </Typography>
                                  </Box>
                              </CardContent>
                          </Card>
                      ))}
            </Box>
            {userData?.subscription_plan === "ORTEX Free" && (
                <Box className={styles["free-account"]}>
                    <Typography variant="body1" component="p">
                        Or continue with your{" "}
                        <Typography
                            variant="body1"
                            component="span"
                            onClick={() => {
                                onClose(false);
                                startTour("subscription_welcome");
                            }}
                        >
                            free account
                        </Typography>
                    </Typography>
                </Box>
            )}
            <Button className={styles.btn} variant="contained" sx={{ textTransform: "initial" }} onClick={() => setStep(2)}>
                Checkout
            </Button>
        </Box>
    );
};

export default PaymentPlans;
